import deepEquals from "fast-deep-equal";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { estateFlowchartApiClient } from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import { getNavItemPage } from "src/EstateFlowchart/components/FormNav/FormNavUtils";
import { EstateFlowchartState } from "src/EstateFlowchart/model/EstateFlowchartTypes";
import {
    familyMembersSelector,
    getEstateFlowchartsSelector,
    getSelectedFlowchart,
    selectedFlowchartIdSelector,
    setGenericErrorModal,
    setSelectedFlowchartId,
    updateEstateFlowchart
} from "src/EstateFlowchart/slices/EstateFlowChartSlice";
import { navItemSelector, setSaveClicked } from "src/EstateFlowchart/slices/NavigationSlice";
import { emptyIrrev, getFamilyMemberByMemberId } from "src/EstateFlowchart/utils/DefUtils";
import { validateForm } from "src/EstateFlowchart/validation/form/IrrevocableTrustFormValidator";
import { ValidationError } from "src/EstateFlowchart/validation/ValidationTypes";
import { RouteWithIdAndFlowchartId } from "src/routes/types";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectReleaseToggles } from "../../../ReleaseToggles/releaseTogglesSlice";
import useAnalytics from "../../../utils/useAnalytics";
import { EstateCardViewModeType } from "../../components/EstateCard/EstateCard";
import Form from "../../components/Form/Form";
import { NavItem } from "../../components/FormNav/NavItem";
import { BENEFICIARIES_AT_FIRST_DEATH_ID } from "../../config/FormPages";
import { BENEFICIARIES_AT_MEMBERS_DEATH, IrrevocableTrustFormPages, } from "../../config/IrrevocableTrustFormPages";
import { IrrevocableTrust } from "../../model/IrrevocableTrust";
import { EstateItemType } from "../EstateItemType";
import IrrevocableTrustContent from "./IrrevocableTrustContent";

const IrrevocableTrustForm = () => {
    const estateFlowchartsInState = useAppSelector(getEstateFlowchartsSelector);
    const location = useLocation();
    const {id, flowchartId, trustId} = useParams<RouteWithIdAndFlowchartId>();
    const dispatch = useAppDispatch();

    const [pageConfig, setPageConfig] = useState<NavItem[]>(IrrevocableTrustFormPages);
    const [linkConfig, setLinkConfig] = useState({});
    const [irrevocableTrust, setIrrevocableTrust] = useState<IrrevocableTrust>(emptyIrrev);
    const [formHasBeenEdited, setFormHasBeenEdited] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [initialDefaultFlowchartName, setInitialDefaultFlowchartName] = useState<string>("");

    const activeNavItem = useAppSelector(navItemSelector);
    const selectedFlowchart = useAppSelector(getSelectedFlowchart)
    const selectedFlowchartId = useAppSelector(selectedFlowchartIdSelector);
    const rawFamilyMembers = useAppSelector(familyMembersSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const flowcharts = useAppSelector(getEstateFlowchartsSelector);

    useEffect(() => {
        if (selectedFlowchart) {
            const existingIrrev = selectedFlowchart.irrevocableTrusts?.find((irrevocableTrust) => irrevocableTrust.trustId === trustId);
            if (existingIrrev) {
                // existing flowchart w/ existing irrev
                const updatedTerminationMember = {
                    firstDeathTerminationMember: existingIrrev.firstDeathTerminationMember ? {
                        ...existingIrrev.firstDeathTerminationMember,
                        memberId: existingIrrev.firstDeathTerminationMember?.familyMember ? existingIrrev.firstDeathTerminationMember.memberId : existingIrrev.firstDeathTerminationMember.customName,
                    } : undefined
                }
                setIrrevocableTrust({
                    ...existingIrrev,
                    firstDeathTerminationMember: releaseToggles?.enableEstateFlowchartMVPRelease ? updatedTerminationMember.firstDeathTerminationMember : existingIrrev.firstDeathTerminationMember,
                    flowchartName: selectedFlowchart.flowchartName ?? ""
                });
            } else {
                // existing flowchart w/ new irrev
                setInitialDefaultFlowchartName(selectedFlowchart?.flowchartName ?? "")
                setIrrevocableTrust({
                    ...irrevocableTrust,
                    flowchartName: selectedFlowchart.flowchartName ?? "",
                    tags: "Exempt",
                    distributions: "",
                    terminatesUponSpecificDeath: false
                });
            }
        } else {
            // new flowchart w/ new irrev
            setInitialDefaultFlowchartName(location.state?.flowchartName ?? "")
            setIrrevocableTrust({
                ...irrevocableTrust,
                flowchartName: location.state.flowchartName ?? "",
                tags: "Exempt",
                distributions: "",
                terminatesUponSpecificDeath: false
            });
        }
    }, [selectedFlowchartId])

    useEffect(() => {
        if (location.state && location?.state?.flowchartName !== "" && location?.state?.estateItem !== "" && selectedFlowchart) {
            const flowchartModelData = location.state;
            const existingIrrev = selectedFlowchart.irrevocableTrusts?.find((irrevocableTrust) => irrevocableTrust.trustId === trustId);
            if (existingIrrev) {
                setIrrevocableTrust(existingIrrev);
            } else {
                setIrrevocableTrust({
                    ...irrevocableTrust,
                    flowchartName: flowchartModelData?.flowchartName,
                    tags: "Exempt",
                    distributions: "",
                    terminatesUponSpecificDeath: false
                })
            }
        }
    }, [location.state, selectedFlowchartId]);

    useEffect(() => {
        // Check if form has been edited
        const defaultIrrevocableTrust = {
            ...emptyIrrev,
            flowchartName: initialDefaultFlowchartName,
            tags: "Exempt"
        } as IrrevocableTrust;
        let initialIrrevocableTrust: IrrevocableTrust = estateFlowchartsInState.find(flowchart =>
            flowchart.flowchartId === flowchartId)?.irrevocableTrusts?.find(irrev => irrev.trustId === irrevocableTrust.trustId) || defaultIrrevocableTrust;
        if (irrevocableTrust.firstDeathTerminationMember === undefined) {
            initialIrrevocableTrust = { ...initialIrrevocableTrust, firstDeathTerminationMember: undefined };
        }
        let irrevocableTrustToCompare = {...irrevocableTrust};
        if (irrevocableTrust.trustId === "") {
            irrevocableTrustToCompare = {
                ...irrevocableTrustToCompare,
                flowchartName: ""
            };
        }

        const hasBeenEdited = !deepEquals(initialIrrevocableTrust, irrevocableTrustToCompare);
        setFormHasBeenEdited(hasBeenEdited);

        // Configure Navigation Jump Links
        let irrevocableTrustLinkConfig: any = {};
        irrevocableTrustLinkConfig[BENEFICIARIES_AT_FIRST_DEATH_ID] = irrevocableTrust?.firstDeathBeneficiaries?.firstDeathDistributions;
        setLinkConfig(irrevocableTrustLinkConfig);
    }, [irrevocableTrust, initialDefaultFlowchartName])

    useEffect(() => {
        if (irrevocableTrust.terminatesUponSpecificDeath && irrevocableTrust.firstDeathTerminationMember?.memberId) {
            setPageConfig([
                ...IrrevocableTrustFormPages,
                BENEFICIARIES_AT_MEMBERS_DEATH(irrevocableTrust.firstDeathTerminationMember, rawFamilyMembers, releaseToggles)
            ]);
        } else {
            setPageConfig(IrrevocableTrustFormPages);
        }
    }, [irrevocableTrust, irrevocableTrust.terminatesUponSpecificDeath, irrevocableTrust.firstDeathTerminationMember?.memberId, releaseToggles]);

    useAnalytics(
        "DEF_IrrevocableTrust_Form",
        "DEF_IrrevocableTrust_Form_TimeSpent",
    );

    const setCommunicationError = (errorStatus: any) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    }

    const checkFormIsValid = () => {
        const pageId = getNavItemPage(activeNavItem)?.id;
        const {
            formHasErrors,
            errors
        } = validateForm(pageId, irrevocableTrust, releaseToggles?.enableEstateFlowchartMVPRelease ?? false, flowcharts, flowchartId);
        setValidationErrors(errors);
        return !formHasErrors;
    }

    const handleCreateNewFlowchartWithIrrevocableTrust = async (): Promise<boolean> => {
        const estateFlowchart: EstateFlowchartState = {
            flowchartId: undefined,
            flowchartName: irrevocableTrust.flowchartName,
            revocableTrust: undefined,
            irrevocableTrusts: [{
                ...irrevocableTrust,
                flowchartName: irrevocableTrust.flowchartName
            }],
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: []
        };
        const response = await estateFlowchartApiClient.createFlowchart(id, estateFlowchart);

        if (response.isSuccessStatusCode) {
            dispatch(setSelectedFlowchartId(response.data.flowchartId));
            dispatch(updateEstateFlowchart(response.data));
            const newIrrevList: IrrevocableTrust[] = response.data.irrevocableTrusts;
            const newIrrevTrust = newIrrevList[newIrrevList.length - 1];
            setIrrevocableTrust({
                ...newIrrevTrust,
                flowchartName: response.data.flowchartName
            });
            return true;
        } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            setCommunicationError(response);
        }

        return false;
    };

    const handleAddIrrevocableTrust = async (): Promise<boolean> => {
        if (selectedFlowchartId) {
            const irrev = {...irrevocableTrust, trustId: ""};
            const response = await estateFlowchartApiClient.createIrrevocableTrust(id, selectedFlowchartId, irrev);

            if (response.isSuccessStatusCode) {
                const newIrrevTrust: IrrevocableTrust = response.data;
                setIrrevocableTrust(newIrrevTrust);
                dispatch(setSelectedFlowchartId(selectedFlowchartId));
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: response.data.flowchartName,
                    irrevocableTrusts: [...(selectedFlowchart?.irrevocableTrusts ?? []), newIrrevTrust]
                } as EstateFlowchartState));
                return true;
            } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                setCommunicationError(response);
            }
        }

        return false;
    };

    // Helper function to handle deleted termination members
    const handleDeletedTerminationMember = (trust: IrrevocableTrust, setNullIfNoFamilyMember: boolean = false): IrrevocableTrust => {
        if (trust.firstDeathTerminationMember && !trust.firstDeathTerminationMember.familyMember) {
            return {
                ...trust,
                firstDeathTerminationMember: {
                    ...trust.firstDeathTerminationMember,
                    memberId: setNullIfNoFamilyMember ? null : trust.firstDeathTerminationMember.customName
                }
            };
        }
        let familyMember: any = getFamilyMemberByMemberId(trust.firstDeathTerminationMember?.memberId, rawFamilyMembers);
        if (familyMember && trust.firstDeathTerminationMember && trust.firstDeathTerminationMember?.customName !== familyMember.firstName + " " + familyMember.lastName) {
            return {
                ...trust,
                firstDeathTerminationMember: {
                    ...trust.firstDeathTerminationMember,
                    customName: ""
                }
            };
        }
        return trust;
    };

    const handleEditIrrevocableTrust = async (): Promise<boolean> => {
        if (formHasBeenEdited) {
            let irrevEdit = handleDeletedTerminationMember({...irrevocableTrust}, true);
            const res = await estateFlowchartApiClient.editIrrevocableTrust(id, flowchartId, irrevEdit);
            if (res.isSuccessStatusCode) {
                const updatedIrrevs = selectedFlowchart?.irrevocableTrusts?.map((irrev) => {
                    if (irrev.trustId === res.data.trustId) {
                        let updatedIrrev: IrrevocableTrust = handleDeletedTerminationMember({...res.data});
                        setIrrevocableTrust(updatedIrrev);
                        return updatedIrrev;
                    }
                    return irrev;
                }) || [];
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: res.data.flowchartName,
                    irrevocableTrusts: updatedIrrevs
                } as EstateFlowchartState));
                return true;
            } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                setCommunicationError(res);
            }
        }
        return false;
    };

    const saveForm = (): Promise<boolean> => {
        dispatch(setSaveClicked(true));
        let handleSave: () => Promise<boolean>;
        if (flowchartId === "new") {
            handleSave = handleCreateNewFlowchartWithIrrevocableTrust;
        } else if (trustId === "new") {
            handleSave = handleAddIrrevocableTrust;
        } else {
            handleSave = handleEditIrrevocableTrust;
        }
        return handleSave();
    };

    return (
        <Form estateItem={irrevocableTrust}
              estateItemType={EstateItemType.IRREVOCABLE_TRUST}
              title={EstateItemType.IRREVOCABLE_TRUST.valueOf()}
              subHeader={irrevocableTrust.flowchartName || undefined}
              pageConfig={pageConfig}
              linkConfig={linkConfig}
              isFormChanged={formHasBeenEdited}
              validate={checkFormIsValid}
              saveForm={saveForm}
              validationErrors={validationErrors}
        >
            <IrrevocableTrustContent
                irrevocableTrust={irrevocableTrust}
                updateIrrevocableTrust={setIrrevocableTrust}
                pageConfig={pageConfig}
                saveForm={saveForm}
            />
        </Form>
    );
};

export default IrrevocableTrustForm;
